/**
 * Firebase client SDK initialisation. The web config is public by design
 * (safe to embed — see MASTER-PROMPT.md §14). In dev, connects to the
 * Firebase Emulator Suite; in production, connects to the live Firebase project.
 * British English project.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getMessaging, isSupported, onMessage, type Messaging } from 'firebase/messaging';
import { sanitizeRedirectPath } from './redirect';

const firebaseConfig = {
  apiKey: 'AIzaSyAcBqs0FzVVxWt7yUlp9rSywkW7LV5X0AM',
  authDomain: 'idea-toolkit.firebaseapp.com',
  projectId: 'idea-toolkit',
  storageBucket: 'idea-toolkit.firebasestorage.app',
  messagingSenderId: '758033455256',
  appId: '1:758033455256:web:efa1f88567af075b4e98ed',
  measurementId: 'G-3PSSJHKTVL',
};

let app: FirebaseApp;
let auth: Auth;
let messaging: Messaging | null = null;
let messagingPromise: Promise<Messaging | null> | null = null;

function setupForegroundMessageHandler(msgInstance: Messaging): void {
  // §9.4 — foreground message handler. When the app is open (in the
  // foreground), FCM delivers the push here instead of to the service
  // worker's onBackgroundMessage. Without this, foreground pushes are
  // silently received but never displayed. Show a notification via the
  // Notification API (works in the foreground; the SW handles background).
  onMessage(msgInstance, (payload) => {
    // Data-only message — title/body are in payload.data (not
    // payload.notification), so the compat SW doesn't auto-display.
    const d = payload.data ?? {};
    const title = (d.title as string | undefined) ?? 'Idea Toolkit';
    const body = (d.body as string | undefined) ?? '';
    const path = sanitizeRedirectPath(d.path, '/book');
    const options = {
      body,
      icon: '/book-idea-review.jpg',
      badge: '/book-idea-review.jpg',
      data: { path },
    };
    // On Android Chrome, `new Notification()` exists but silently doesn't
    // display. Use the service worker's showNotification instead, which
    // works cross-platform. Fall back to new Notification() if the SW
    // registration isn't available (rare — the SW should be registered
    // by getToken in push.ts before any push arrives).
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.ready
        .then((reg) => reg.showNotification(title, options))
        .catch(() => {});
    } else {
      try {
        const n = new Notification(title, options);
        n.onclick = () => {
          window.focus();
          n.close();
          window.location.assign(path);
        };
      } catch {
        // Non-fatal.
      }
    }
  });
}

function init(): void {
  if (app) return;
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);

  // Eagerly start checking FCM support in the background.
  void getPushMessaging();

  // In dev, point the client at the emulators (if running).
  if (import.meta.env.DEV && (import.meta.env.VITE_USE_EMULATORS === 'true')) {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { connectAuthEmulator } = require('firebase/auth');
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  }
}

init();

export { app as firebaseApp, auth };

/** The FCM Messaging instance promise, resolving to Messaging or null if unsupported. */
export async function getPushMessaging(): Promise<Messaging | null> {
  if (messaging) return messaging;
  if (!messagingPromise) {
    messagingPromise = isSupported()
      .then((supported) => {
        if (supported) {
          messaging = getMessaging(app);
          setupForegroundMessageHandler(messaging);
          return messaging;
        }
        return null;
      })
      .catch(() => null);
  }
  return messagingPromise;
}

/** Synchronous accessor if already initialised. */
export function getPushMessagingSync(): Messaging | null {
  return messaging;
}